/** Progressive feedback for Shopify's native contact form and CAPTCHA flow. */
class NexitaContactForm extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.form = this.querySelector('form');
    this.button = this.form?.querySelector('[type="submit"]');
    if (!this.form || !this.button) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.originalLabel = this.button.textContent.trim();
    this.pending = this.querySelector('[data-contact-pending]');
    this.notice = this.querySelector('[data-contact-notice]');

    this.form.addEventListener('invalid', this.onInvalid, { capture: true, signal });
    this.form.addEventListener('input', this.onInput, { signal });
    // Capture before Shopify's submit listener; valid submissions retain the native flow.
    this.form.addEventListener('submit', this.onSubmit, { capture: true, signal });
    window.addEventListener('pageshow', this.resetPending, { signal });
    window.addEventListener('pagehide', () => clearTimeout(this.pendingTimer), { signal });
    window.addEventListener('offline', this.onOffline, { signal });

    const result = this.querySelector('[data-contact-result]');
    if (result) {
      const reveal = () => requestAnimationFrame(() => this.reveal(result));
      if (document.readyState === 'complete') reveal();
      else window.addEventListener('load', reveal, { once: true, signal });
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.controller = null;
    clearTimeout(this.pendingTimer);
    cancelAnimationFrame(this.invalidFrame);
  }

  reveal = (element) => {
    element.focus({ preventScroll: true });
    element.scrollIntoView({ block: 'center', behavior: 'instant' });
  };

  showFieldError = (field) => {
    const message = field.validity.typeMismatch ? field.dataset.invalidMessage : field.dataset.requiredMessage;
    const error = this.querySelector(`#${CSS.escape(field.id)}-error`);
    field.setAttribute('aria-invalid', 'true');
    if (error) {
      error.textContent = message || field.validationMessage;
      error.hidden = false;
    }
  };

  onInvalid = (event) => {
    const field = event.target;
    if (!field.matches('.contact-form__input')) return;
    event.preventDefault();
    this.showFieldError(field);
    cancelAnimationFrame(this.invalidFrame);
    this.invalidFrame = requestAnimationFrame(() => {
      const first = this.form.querySelector(':invalid');
      if (first) this.reveal(first);
    });
  };

  onInput = (event) => {
    const field = event.target;
    if (!field.matches('.contact-form__input')) return;
    field.setCustomValidity('');
    if (field.required && field.value && !field.value.trim()) {
      field.setCustomValidity(field.dataset.requiredMessage);
    }
    if (field.getAttribute('aria-invalid') !== 'true') return;
    if (!field.validity.valid) return this.showFieldError(field);
    field.removeAttribute('aria-invalid');
    const error = this.querySelector(`#${CSS.escape(field.id)}-error`);
    if (error) { error.hidden = true; error.textContent = ''; }
  };

  onSubmit = (event) => {
    if (this.sending) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    if (!navigator.onLine) {
      event.preventDefault();
      event.stopImmediatePropagation();
      this.showNotice(this.notice.dataset.offlineMessage);
      return;
    }
    // Covers autofilled or restored whitespace-only values as well as typing.
    for (const field of this.form.querySelectorAll('[required]')) {
      field.setCustomValidity(field.value.trim() ? '' : field.dataset.requiredMessage);
    }
    if (!this.form.checkValidity()) {
      event.preventDefault();
      event.stopImmediatePropagation();
      return;
    }
    this.notice.hidden = true;
    this.sending = true;
    this.button.disabled = true;
    this.button.setAttribute('aria-busy', 'true');
    this.button.textContent = this.querySelector('[data-contact-pending-text]').textContent;
    this.pending.hidden = false;
    // Shopify handles submission and CAPTCHA. A timeout is never treated as success.
    this.pendingTimer = setTimeout(() => {
      this.resetPending();
      this.showNotice(this.notice.dataset.slowMessage);
    }, 60000);
  };

  resetPending = () => {
    clearTimeout(this.pendingTimer);
    this.sending = false;
    this.button.disabled = false;
    this.button.removeAttribute('aria-busy');
    this.button.textContent = this.originalLabel;
    this.pending.hidden = true;
  };

  showNotice = (message) => {
    this.notice.textContent = message;
    this.notice.hidden = false;
    this.reveal(this.notice);
  };

  onOffline = () => {
    if (!this.sending) return;
    this.resetPending();
    this.showNotice(this.notice.dataset.offlineMessage);
  };
}

if (!customElements.get('nexita-contact-form')) {
  customElements.define('nexita-contact-form', NexitaContactForm);
}
